import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../context/AuthContext';
import apiService from '../../services/api';
import SimplePasswordChangeModal from './SimplePasswordChangeModal';
import logger from '../../utils/logging/logger';
import { AuthPage, AuthCard } from './AuthLayout';

const Container = styled.div`
  width: 100%;
`;

const Title = styled.h2`
  color: var(--primary-color);
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  text-align: center;
`;

const Subtitle = styled.p`
  color: var(--text-secondary);
  font-size: 0.875rem;
  text-align: center;
  margin-bottom: 2rem;
`;

function PasswordChangeRequired() {
  const [showPasswordChangeModal, setShowPasswordChangeModal] = useState(true);
  const { markPasswordChanged, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  // null while the server answers; true (ask for the current password) unless it says this is a brand-new guest student.
  const [requiresCurrentPassword, setRequiresCurrentPassword] = useState(null);

  useEffect(() => {
    let cancelled = false;
    apiService.getChangePasswordStatus()
      .then((status) => { if (!cancelled) setRequiresCurrentPassword(status?.requiresCurrentPassword !== false); })
      .catch(() => { if (!cancelled) setRequiresCurrentPassword(true); });
    return () => { cancelled = true; };
  }, []);

  const handlePasswordChangeSuccess = () => {
    logger.debug('Password changed successfully');
    setShowPasswordChangeModal(false);
    markPasswordChanged();
    // The App.js will automatically redirect to dashboard since password_change_required will be false
  };

  const handleCloseModal = async () => {
    // A new student who came here from "Set my password" on the confirmation page goes back to it (the browser
    // history entry still holds their order); they are not signed out. Anyone else is, as before.
    if (location.state?.fromConfirmation) {
      navigate(-1);
      return;
    }
    if (requiresCurrentPassword === false) {
      navigate('/confirmation', { replace: true });
      return;
    }
    logger.debug('Closing password change modal and logging out');
    setShowPasswordChangeModal(false);
    await logout();
    // User will be redirected to login page automatically
  };

  // A new student's window already says "Set Your Password" and why, so the page behind it carries only the
  // logo (from App.jsx) - no second, nested card repeating the same title.
  if (requiresCurrentPassword === false) {
    return (
      <SimplePasswordChangeModal
        isOpen={showPasswordChangeModal}
        onClose={handleCloseModal}
        onSuccess={handlePasswordChangeSuccess}
        requireCurrentPassword={false}
      />
    );
  }

  return (
    <AuthPage>
      <AuthCard>
        <Container>
          <Title>Password Change Required</Title>
          <Subtitle>For security reasons, you must change your password before continuing.</Subtitle>

          {requiresCurrentPassword !== null && (
          <SimplePasswordChangeModal
            isOpen={showPasswordChangeModal}
            onClose={handleCloseModal} // Allow closing - will log user out and return to login
            onSuccess={handlePasswordChangeSuccess}
            requireCurrentPassword={requiresCurrentPassword}
          />
          )}
        </Container>
      </AuthCard>
    </AuthPage>
  );
}

export default PasswordChangeRequired;
